import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Play, Loader2, Zap, Clock, CheckCircle2, XCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reglas Guardián - Motor de automatizaciones.
 * Reutilizable: se le pasa el LayoutComponent (admin o trainer) y el clientsEndpoint
 * (quién es el universo de clientes para el botón "probar").
 */
const AutomationRulesPage = ({ LayoutComponent, clientsEndpoint = '/clients' }) => {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ triggers: [], actions: [], roles: [], cooldowns: [] });
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [clients, setClients] = useState([]);
  const [pildoras, setPildoras] = useState([]);

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', trigger_type: '', trigger_config: {},
    action_type: '', action_config: {}, active: true,
    rol: '', priority: 0, cooldown_id: 'once_per_day'
  });
  const [submitting, setSubmitting] = useState(false);

  // Test dialog
  const [testingRule, setTestingRule] = useState(null);
  const [testClient, setTestClient] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [catRes, rulesRes, logsRes, roadmapRes, clientsRes, pilRes] = await Promise.all([
        axios.get(`${API}/automation/catalog`),
        axios.get(`${API}/automation/rules`),
        axios.get(`${API}/automation/logs`),
        axios.get(`${API}/automation/roadmap`),
        axios.get(`${API}${clientsEndpoint}`),
        axios.get(`${API}/pildoras-progresivas`),
      ]);
      setCatalog(catRes.data);
      setRules(rulesRes.data.rules || []);
      setLogs(logsRes.data.logs || []);
      setRoadmap(roadmapRes.data);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data.clients || []));
      setPildoras(pilRes.data.pildoras || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando');
    } finally {
      setLoading(false);
    }
  };

  const rolMap = useMemo(() => Object.fromEntries((catalog.roles || []).map(r => [r.id, r])), [catalog]);

  const triggerMap = useMemo(() => Object.fromEntries((catalog.triggers || []).map(t => [t.id, t])), [catalog]);
  const actionMap = useMemo(() => Object.fromEntries((catalog.actions || []).map(a => [a.id, a])), [catalog]);

  const resetForm = () => {
    setForm({ name: '', description: '', trigger_type: '', trigger_config: {}, action_type: '', action_config: {}, active: true, rol: '', priority: 0, cooldown_id: 'once_per_day' });
    setEditing(null);
  };

  const openNew = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      name: rule.name,
      description: rule.description || '',
      trigger_type: rule.trigger_type,
      trigger_config: rule.trigger_config || {},
      action_type: rule.action_type,
      action_config: rule.action_config || {},
      active: rule.active,
      rol: rule.rol || '',
      priority: rule.priority || 0,
      cooldown_id: rule.cooldown_id || 'once_per_day',
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.trigger_type || !form.action_type) {
      toast.error('Completa nombre, trigger y acción');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/automation/rules/${editing.id}`, form);
        toast.success('Regla actualizada');
      } else {
        await axios.post(`${API}/automation/rules`, form);
        toast.success('Regla creada');
      }
      setShowDialog(false);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`¿Borrar regla "${rule.name}"?`)) return;
    try {
      await axios.delete(`${API}/automation/rules/${rule.id}`);
      toast.success('Eliminada');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleActive = async (rule) => {
    try {
      await axios.put(`${API}/automation/rules/${rule.id}`, { active: !rule.active });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const runTest = async () => {
    if (!testClient) {
      toast.error('Selecciona un cliente');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/automation/rules/${testingRule.id}/test`, { client_id: testClient });
      toast.success(res.data.result?.message || 'OK');
      setTestingRule(null);
      setTestClient('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setTesting(false); }
  };

  // Render params dinámicos del trigger/action seleccionado
  const renderParams = (catalogItem, configKey) => {
    if (!catalogItem) return null;
    const cfg = form[configKey] || {};
    const update = (k, v) => setForm(p => ({ ...p, [configKey]: { ...p[configKey], [k]: v } }));
    return (catalogItem.params || []).map(p => (
      <div key={p.key}>
        <Label className="text-xs">{p.label}</Label>
        {p.type === 'textarea' ? (
          <Textarea value={cfg[p.key] ?? p.default ?? ''} onChange={(e) => update(p.key, e.target.value)}
            rows={3} className="bg-zinc-900/50 border-zinc-800" />
        ) : p.type === 'pildora_select' ? (
          <Select value={cfg[p.key] || ''} onValueChange={(v) => update(p.key, v)}>
            <SelectTrigger className="bg-zinc-900/50 border-zinc-800"><SelectValue placeholder="Selecciona píldora" /></SelectTrigger>
            <SelectContent>
              {pildoras.map(pi => (
                <SelectItem key={pi.id} value={pi.id}>#{pi.numero_orden} - {pi.titulo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : p.type === 'number' ? (
          <Input type="number" value={cfg[p.key] ?? p.default ?? ''} onChange={(e) => update(p.key, e.target.value)}
            className="bg-zinc-900/50 border-zinc-800" />
        ) : (
          <Input value={cfg[p.key] ?? p.default ?? ''} onChange={(e) => update(p.key, e.target.value)}
            className="bg-zinc-900/50 border-zinc-800" />
        )}
      </div>
    ));
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-ES') : '';

  return (
    <LayoutComponent>
      <div className="space-y-6" data-testid="automation-rules-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="h-6 w-6 text-amber-400" /> Reglas Guardián
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Automatiza mensajes, notificaciones y acciones cuando pasan cosas con tus clientes.
            </p>
          </div>
          <Button onClick={openNew} data-testid="create-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva Regla
          </Button>
        </div>

        <Tabs defaultValue="rules">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="rules">Mis Reglas ({rules.length})</TabsTrigger>
            <TabsTrigger value="roadmap">Proceso</TabsTrigger>
            <TabsTrigger value="logs">Historial ({logs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="mt-4">
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : rules.length === 0 ? (
              <Card className="bg-card border-dashed">
                <CardContent className="py-12 text-center">
                  <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-4">Aún no tienes reglas. Automatiza tu trabajo.</p>
                  <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Crear primera regla</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {rules.map(rule => {
                  const t = triggerMap[rule.trigger_type];
                  const a = actionMap[rule.action_type];
                  return (
                    <Card key={rule.id} className="bg-card border-border">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Switch checked={rule.active} onCheckedChange={() => toggleActive(rule)}
                                data-testid={`toggle-${rule.id}`} />
                              <h3 className="font-semibold text-foreground">{rule.name}</h3>
                              {rule.rol && rolMap[rule.rol] && (
                                <Badge variant="outline" className="text-xs">{rolMap[rule.rol].label}</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">P{rule.priority || 0}</Badge>
                              <Badge variant="outline" className="text-xs">
                                {rule.owner_role === 'admin' ? 'Global' : 'Mis clientes'}
                              </Badge>
                              {!rule.active && <Badge variant="outline" className="text-xs border-zinc-700 text-muted-foreground">Inactiva</Badge>}
                            </div>
                            {rule.description && <p className="text-xs text-muted-foreground mb-2">{rule.description}</p>}
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                              <Badge variant="outline" className="border-blue-500/40 text-blue-400">
                                SI: {t?.label || rule.trigger_type}
                              </Badge>
                              <span className="text-muted-foreground">→</span>
                              <Badge variant="outline" className="border-amber-500/40 text-amber-400">
                                ENTONCES: {a?.label || rule.action_type}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setTestingRule(rule); setTestClient(''); }}
                              data-testid={`test-${rule.id}`}>
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(rule)} data-testid={`edit-${rule.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(rule)}
                              data-testid={`delete-${rule.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="roadmap" className="mt-4 space-y-4">
            {roadmap ? (
              <>
                {roadmap.empty_roles?.length > 0 && (
                  <Card className="bg-amber-500/5 border-amber-500/30">
                    <CardContent className="p-3 text-sm">
                      <span className="text-amber-400 font-medium">Huecos del proceso:</span>{' '}
                      <span className="text-muted-foreground">{roadmap.empty_roles.join(' · ')}</span>
                    </CardContent>
                  </Card>
                )}
                {roadmap.conflicts?.length > 0 && (
                  <Card className="bg-red-500/5 border-red-500/30">
                    <CardContent className="p-3 text-sm">
                      <p className="text-red-400 font-medium mb-1">⚠️ Conflictos detectados</p>
                      {roadmap.conflicts.map((c, i) => (
                        <div key={i} className="text-xs text-muted-foreground">
                          Trigger "{c.trigger}": {c.rules.map(r => r.name).join(', ')} — {c.reason}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(catalog.roles || []).map(rol => {
                    const group = roadmap.by_rol?.[rol.id];
                    const rulesInRol = group?.rules || [];
                    return (
                      <Card key={rol.id} className="bg-card border-border">
                        <CardHeader>
                          <CardTitle className="text-sm flex items-center justify-between">
                            <span>{rol.label}</span>
                            <Badge variant="outline" className="text-xs">{rulesInRol.length}</Badge>
                          </CardTitle>
                          <p className="text-xs text-muted-foreground">{rol.description}</p>
                        </CardHeader>
                        <CardContent>
                          {rulesInRol.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">Sin reglas en esta fase</p>
                          ) : (
                            <div className="space-y-1.5">
                              {rulesInRol.map(r => (
                                <div key={r.id} className="flex items-center gap-2 text-xs p-1.5 bg-zinc-900/50 rounded">
                                  <Badge variant="outline" className="text-[10px]">P{r.priority || 0}</Badge>
                                  <span className="flex-1 truncate">{r.name}</span>
                                  {r.active ? <span className="text-green-400">●</span> : <span className="text-zinc-600">○</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : <Loader2 className="h-6 w-6 animate-spin" />}
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            {logs.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-6 text-center">Aún no se han ejecutado reglas</p>
            ) : (
              <div className="space-y-2">
                {logs.map(log => (
                  <Card key={log.id} className={`bg-card ${log.success ? 'border-green-500/20' : 'border-red-500/30'}`}>
                    <CardContent className="p-3 text-sm">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {log.success ? <CheckCircle2 className="h-4 w-4 text-green-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                        <span className="font-medium">{log.rule_name}</span>
                        <span className="text-muted-foreground">→ {log.client_name}</span>
                        <Badge variant="outline" className="text-xs ml-auto">{log.trigger_type}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />{fmtDate(log.executed_at)}
                        {log.action_result?.message && <span className="italic">· {log.action_result.message}</span>}
                        {log.error && <span className="text-red-400">· {log.error}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit dialog */}
        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar' : 'Nueva'} Regla</DialogTitle>
              <DialogDescription className="text-xs">
                Define un trigger y una acción. La regla se ejecutará automáticamente cuando ocurra el evento.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ej: Bienvenida al completar primera sesión"
                  className="bg-zinc-900/50 border-zinc-800" data-testid="form-name" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={2} className="bg-zinc-900/50 border-zinc-800" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-zinc-900/30 border border-zinc-800 rounded">
                <div>
                  <Label className="text-xs">Fase / Rol</Label>
                  <Select value={form.rol || ''} onValueChange={(v) => setForm(p => ({ ...p, rol: v }))}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="form-rol">
                      <SelectValue placeholder="Fase del proceso" />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.roles?.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Prioridad</Label>
                  <Input type="number" value={form.priority}
                    onChange={(e) => setForm(p => ({ ...p, priority: parseInt(e.target.value || '0', 10) }))}
                    placeholder="0"
                    className="bg-zinc-900/50 border-zinc-800" data-testid="form-priority" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Mayor = se ejecuta antes</p>
                </div>
                <div>
                  <Label className="text-xs">Frecuencia</Label>
                  <Select value={form.cooldown_id} onValueChange={(v) => setForm(p => ({ ...p, cooldown_id: v }))}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="form-cooldown">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalog.cooldowns?.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 space-y-2">
                <Label className="text-blue-400">SI: Trigger</Label>
                <Select value={form.trigger_type} onValueChange={(v) => setForm(p => ({ ...p, trigger_type: v, trigger_config: {} }))}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="form-trigger">
                    <SelectValue placeholder="Elige un evento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.triggers.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.trigger_type && (
                  <>
                    <p className="text-xs text-muted-foreground italic">{triggerMap[form.trigger_type]?.description}</p>
                    {renderParams(triggerMap[form.trigger_type], 'trigger_config')}
                  </>
                )}
              </div>

              <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 space-y-2">
                <Label className="text-amber-400">ENTONCES: Acción</Label>
                <Select value={form.action_type} onValueChange={(v) => setForm(p => ({ ...p, action_type: v, action_config: {} }))}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="form-action">
                    <SelectValue placeholder="Elige una acción..." />
                  </SelectTrigger>
                  <SelectContent>
                    {catalog.actions.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.action_type && (
                  <>
                    <p className="text-xs text-muted-foreground italic">{actionMap[form.action_type]?.description}</p>
                    {renderParams(actionMap[form.action_type], 'action_config')}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch id="active" checked={form.active} onCheckedChange={(v) => setForm(p => ({ ...p, active: v }))} />
                <Label htmlFor="active" className="cursor-pointer">Activa</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="form-save">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test dialog */}
        <Dialog open={!!testingRule} onOpenChange={(o) => { if (!o) { setTestingRule(null); setTestClient(''); } }}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Probar regla "{testingRule?.name}"</DialogTitle>
              <DialogDescription className="text-xs">
                Ejecutará la acción directamente sobre el cliente que elijas (ignora el trigger).
              </DialogDescription>
            </DialogHeader>
            <Select value={testClient} onValueChange={setTestClient}>
              <SelectTrigger className="bg-zinc-900/50 border-zinc-800"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setTestingRule(null); setTestClient(''); }}>Cancelar</Button>
              <Button onClick={runTest} disabled={testing || !testClient} data-testid="run-test-btn">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Probar ahora'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LayoutComponent>
  );
};

export default AutomationRulesPage;
